import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import {
  accessToken,
  refreshToken,
  replaceTokens,
  sessionExpired,
  type IssuedTokens,
} from "@/lib/auth";
import { apiErrorCode } from "@/lib/api-error";
import { panelTimeZone } from "@/lib/timezone";
import type { RootState } from "./store";

/**
 * The single data layer for the whole panel.
 *
 * Every feature injects its endpoints into this one API object rather than
 * creating its own. That is what makes cross-feature invalidation work — when
 * a role's permissions change, the admin roster refetches because they share a
 * tag registry, with no manual coordination.
 *
 * Corollary: no hand-rolled thunks for CRUD. If you are writing
 * `dispatch(fetchAdmins())`, it belongs here as a query.
 *
 * ---------------------------------------------------------------------------
 * This talks to the real API.
 *
 * `VITE_API_BASE_URL` + `/v1/admin`, with a bearer token from `lib/auth`. The
 * routes the API actually serves today are the profile (`/me`), admin accounts
 * (`/admins`, `/sub-admins`), rights (`/roles`, `/permissions`), the activity
 * log (`/audit-log`, `/activity`), player accounts and moderation
 * (`/players/:id/…`, `/bans`) and notifications (`/notifications/…`) — those
 * are the screens wired to it.
 *
 * The rest of the panel's sections have no routes on the server yet. They are
 * routed to a "coming soon" page rather than left pointed at fixtures, because
 * a screen full of invented numbers is indistinguishable from a screen full of
 * real ones, and that is exactly the confusion this swap was meant to end.
 * `src/mocks` is no longer wired to anything; it is kept on disk only as the
 * reference for the shapes those routes will return.
 * ---------------------------------------------------------------------------
 */

/**
 * Where the API lives.
 *
 * Trailing slashes are stripped so `…:3000/` and `…:3000` produce the same
 * URL — with one left on, every request path would carry a double slash, which
 * Fastify's router treats as a different route and answers with a 404.
 */
const ORIGIN = (import.meta.env.VITE_API_BASE_URL ?? "").trim().replace(/\/+$/, "");

export const API_BASE_URL = `${ORIGIN}/v1/admin`;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,

  prepareHeaders: (headers) => {
    // Read per request rather than captured once at module load: the token can
    // change without a reload (a token sign-in, a sign-out), and a captured
    // one would keep authenticating as whoever loaded the page.
    const token = accessToken();
    if (token) headers.set("authorization", `Bearer ${token}`);
    // The zone the API reads wall clocks and dates in, and answers `*_local`
    // in. Per request for the same reason: the browser's zone can change under
    // an open tab, and the saved preference arrives with `/me`.
    headers.set("x-timezone", panelTimeZone());
    return headers;
  },
});

/**
 * The pre-token routes: sign-in and refresh. No `Authorization` header — the
 * whole point of calling them is not holding a usable token.
 */
export const PUBLIC_ADMIN_AUTH_URL = `${ORIGIN}/v1/public/admin/auth`;

const publicAuthQuery = fetchBaseQuery({
  baseUrl: PUBLIC_ADMIN_AUTH_URL,
  prepareHeaders: (headers) => {
    headers.set("x-timezone", panelTimeZone());
    return headers;
  },
});

/** The refresh in flight, shared by every request that hit a 401 meanwhile. */
let refreshing: Promise<boolean> | null = null;

/**
 * Trades the refresh token for a new pair. True if the session was renewed.
 *
 * Refresh tokens are single use and the API treats a reused one as theft,
 * ending the session. So a page firing six queries that all come back 401 must
 * refresh *once*: they share `refreshing`. Across tabs the same rule holds, so
 * the exchange runs under a Web Lock — a tab that waited finds the token
 * already replaced by the other tab and simply uses the new one.
 */
async function refreshSession(
  api: Parameters<BaseQueryFn>[1],
  extraOptions: Parameters<BaseQueryFn>[2],
): Promise<boolean> {
  const attempt = async (): Promise<boolean> => {
    const token = refreshToken();
    if (!token) return false;

    const result = await publicAuthQuery(
      { url: "/refresh", method: "POST", body: { refresh_token: token } },
      api,
      extraOptions,
    );

    // Another tab finished its own refresh while this one was asking. Its
    // pair is the live one; this one's answer is a refusal of a spent token.
    if (refreshToken() !== token) return refreshToken() !== null;

    if (result.data) {
      replaceTokens(result.data as IssuedTokens);
      return true;
    }
    return false;
  };

  const tokenBefore = refreshToken();
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!locks) return attempt();

  return locks.request("aa.refresh", async () => {
    // Waited behind another tab that has already renewed the session.
    const current = refreshToken();
    if (current !== null && current !== tokenBefore) return true;
    return attempt();
  });
}

/**
 * The base query, plus one thing `fetchBaseQuery` cannot do on its own.
 *
 * A 401 means the access token is no longer good — expired, most often. The
 * refresh token is traded for a new pair and the request retried once. If
 * there is no refresh token, the refresh is refused, or the retry is refused
 * too, the session is over: it is cleared and the browser goes to sign-in.
 *
 * Doing this here rather than in the screen that happened to make the request
 * is what stops the panel retrying a dead token on every query on the page and
 * collecting a row of identical errors.
 */
const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  // Sign-in's own refusals (a wrong password, a wrong code) are 401s too, and
  // are answers for the form — not a session to renew.
  const url = typeof args === "string" ? args : args.url;
  if (url.startsWith(PUBLIC_ADMIN_AUTH_URL)) {
    // Endpoints name the full URL so this check can see them; the public query
    // already has it as its base. Handed over whole, a relative origin (a blank
    // VITE_API_BASE_URL) is not "absolute" to fetchBaseQuery, so it would join
    // the base on twice: `/v1/public/admin/auth/v1/public/admin/auth/login`.
    const path = url.slice(PUBLIC_ADMIN_AUTH_URL.length);
    const publicArgs = typeof args === "string" ? path : { ...args, url: path };
    return publicAuthQuery(publicArgs, api, extraOptions);
  }

  const sentWith = accessToken();
  let result = await rawBaseQuery(args, api, extraOptions);

  if (result.error?.status !== 401 || sentWith === null) return result;

  // The server ended this session on purpose — a logout elsewhere, a password
  // change, a suspension. Its refresh token died with it, so trading it in
  // would only cost a round trip before landing in the same place.
  if (apiErrorCode(result.error) === "SESSION_REVOKED") {
    sessionExpired();
    return result;
  }

  // A refresh finished while this request was in flight: its 401 was for the
  // old token, so try again with the new one before refreshing anything.
  const current = accessToken();
  let renewed = current !== null && current !== sentWith;

  if (!renewed) {
    refreshing ??= refreshSession(api, extraOptions).finally(() => {
      refreshing = null;
    });
    renewed = await refreshing;
  }

  if (renewed) {
    result = await rawBaseQuery(args, api, extraOptions);
    if (result.error?.status !== 401) return result;
  }

  sessionExpired();
  return result;
};

/**
 * Tag registry.
 *
 * One tag per resource the panel can mutate. A mutation lists what it
 * invalidates and the affected queries refetch — this is the entire cache
 * coherence strategy, and it only works if every endpoint declares its tags
 * honestly.
 */
export const API_TAGS = [
  "Player",
  "PlayerList",
  "Ban",
  "Guild",
  "Report",
  "Character",
  "Skin",
  "ConfigVersion",
  "Mission",
  "Reward",
  "Progression",
  "Wallet",
  "LedgerEntry",
  "Inventory",
  "StoreProduct",
  "StoreOffer",
  "Purchase",
  "Tournament",
  "Bracket",
  "Leaderboard",
  "Ranking",
  "Match",
  "Quarantine",
  "MatchmakingConfig",
  "Announcement",
  "Notification",
  "Admin",
  "SubAdmin",
  "Profile",
  "Role",
  "AuditLog",
  "SecuritySignal",
  "Analytics",
] as const;

export const api = createApi({
  reducerPath: "api",
  baseQuery,
  tagTypes: API_TAGS,
  // Analytics dashboards are expensive to compute server-side and change
  // slowly; 60s of cache turns a tab-switch into a no-op instead of a query.
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: 30,
  refetchOnReconnect: true,
  endpoints: () => ({}),
});

export type { RootState };
